import React, { useState } from "react";
import { Box, Tabs, Tab, IconButton } from "@mui/material";
import { InfoOutline, Search } from "@tiny/icons";
import TinyTable from "../../components/Table";
import { InfoTooltip, Panel } from "../../components";
import { TableHeaderCellsTypes } from "../../components/Table/table.types";
import ComponentLoader from "../../components/ComponentLoader/ComponentLoader";
import { useTopEngagedPanel } from "./TopEngagedPanel.hooks";
import EmptyState from "../../components/EmptyState";
import { Theme } from "@mui/material";

export type TopEngagedRow = {
  name: string | React.ReactNode;
  sentEmails: string;
  pnRatio: string;
  openRate: string;
  clickRate: string;
  spamRate: string;
  unsubscribe: string;
};

export interface TopEngagedPanelProps {
  isLoading?: boolean;
}

const headerCells: TableHeaderCellsTypes<TopEngagedRow>[] = [
  { propName: "name", label: "Name", isSortable: false, width: "300px" },
  { propName: "sentEmails", label: "Sent Emails", isSortable: false },
  // { propName: "pnRatio", label: "PN Ratio", isSortable: false },
  { propName: "openRate", label: "Open Rate", isSortable: false },
  { propName: "clickRate", label: "Click Rate", isSortable: false },
  { propName: "spamRate", label: "Spam Rate", isSortable: false },
  { propName: "unsubscribe", label: "Unsubscribe", isSortable: false },
];

function TopEngagedPanel({ isLoading = false }: TopEngagedPanelProps) {
  const {
    tabValue,
    handleTabChange,
    campaignRows,
    workflowRows,
    isLoadingCampaigns,
    isLoadingWorkflows,
  } = useTopEngagedPanel();

  const showLoader = isLoading || isLoadingCampaigns || isLoadingWorkflows;

  const renderEmptyState = () => (
    <EmptyState
      Icon={
        <Search
          sx={(theme: Theme) => ({
            color: "grey.80",
            width: theme.spacing(6),
            height: theme.spacing(6),
          })}
        />
      }
      title="No data available"
      message="You need to start a campaign or a workflow to gather data"
    />
  );

  return (
    <Panel
      title="Top Engaged (data from last 90 days)"
      actions={
        <InfoTooltip
          message="Displays the most engaging campaigns and workflows from the past 90 days based on user interactions."
          variant="white"
          noInfoIcon
        >
          <IconButton sx={{ p: 0, ml: 1 }}>
            <InfoOutline />
          </IconButton>
        </InfoTooltip>
      }
    >
      {showLoader ? (
        <ComponentLoader height="400px" />
      ) : (
        <Box sx={{ width: "100%" }} mt={6}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }} px={5}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="engagement tabs"
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Campaigns" id="tab-campaigns" />
              <Tab label="Workflows" id="tab-workflows" />
            </Tabs>
          </Box>

          <Box
            role="tabpanel"
            hidden={tabValue !== 0}
            id="tabpanel-campaigns"
            aria-labelledby="tab-campaigns"
          >
            {tabValue === 0 &&
              (campaignRows.length === 0 ? (
                renderEmptyState()
              ) : (
                <TinyTable
                  headerCells={headerCells}
                  rowsData={campaignRows}
                  rows={campaignRows}
                  options={{
                    currentSort: "name.asc",
                    onSort: () => {},
                    showActionAlways: false,
                    showBorders: true,
                  }}
                />
              ))}
          </Box>

          <Box
            role="tabpanel"
            hidden={tabValue !== 1}
            id="tabpanel-workflows"
            aria-labelledby="tab-workflows"
          >
            {tabValue === 1 &&
              (workflowRows.length === 0 ? (
                renderEmptyState()
              ) : (
                <TinyTable
                  headerCells={headerCells}
                  rowsData={workflowRows}
                  rows={workflowRows}
                  options={{
                    currentSort: "name.asc",
                    onSort: () => {},
                    showActionAlways: false,
                  }}
                />
              ))}
          </Box>
        </Box>
      )}
    </Panel>
  );
}

export default TopEngagedPanel;
